package org.vreo.devicehub.features.liveAccess.domain

import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.awaitAnimationFrame
import kotlinx.coroutines.channels.BufferOverflow
import kotlin.coroutines.resume
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.coroutines.Job

import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.toComposeImageBitmap
import kotlinx.coroutines.ExperimentalCoroutinesApi

import org.jetbrains.skia.ColorAlphaType
import org.jetbrains.skia.ColorType
import org.jetbrains.skia.Image
import org.jetbrains.skia.ImageInfo

import web.canvas.CanvasRenderingContext2D
import web.canvas.ID
import web.dom.document
import web.events.EventHandler
import web.html.HTMLCanvasElement
import web.html.HTMLVideoElement
import web.rtc.RTCRtpTransceiverDirection
import web.rtc.RTCRtpTransceiverInit
import web.rtc.recvonly
import web.rtc.RTCLocalSessionDescriptionInit
import web.rtc.RTCPeerConnection
import web.rtc.createOffer
import web.rtc.setLocalDescription
import web.rtc.setRemoteDescription
import web.rtc.RTCIceGatheringState
import web.rtc.complete
import web.html.HTMLMediaElement
import web.mediastreams.MediaStream

import org.vreo.devicehub.features.core.data.appLogger.AppLogger
import org.vreo.devicehub.features.core.domain.device.webRtc.WebRtcOfferAnswer
import kotlin.time.Duration.Companion.milliseconds


class JsWebRtcController(
    private val targetFps: Double = 6.0,
    private var connectionMode: WebRtcConnectMode = WebRtcConnectMode.NORMAL
) : WebRtcController {
    // Local instance of the RTCPeerConnection object

    private var peerConnection: RTCPeerConnection? = null
    private val videoElement: HTMLVideoElement = document.createElement("video") as HTMLVideoElement
    private var streamingJob: Job? = null
    private var framePumpStarted = false
    private var remoteStream = MediaStream()
    private val videoFrames = MutableSharedFlow<ImageBitmap>(
        replay = 1,
        extraBufferCapacity = 2,
        onBufferOverflow = BufferOverflow.DROP_OLDEST
    )
    private val validVideoStates = listOf(
        HTMLMediaElement.HAVE_CURRENT_DATA,
        HTMLMediaElement.HAVE_FUTURE_DATA,
        HTMLMediaElement.HAVE_ENOUGH_DATA
    )

    init {
        initializePeerConnection(connectionMode)
    }

    override fun getMode(): WebRtcConnectMode {
        return connectionMode
    }

    override suspend fun setMode(mode: WebRtcConnectMode) {
        if (connectionMode == mode) {
            return
        }
        connectionMode = mode
        disconnect()
    }

    override suspend fun createOffer(): WebRtcOfferAnswer {
        // Re-initialize peer connection if not initialized
        if (peerConnection == null) {
            initializePeerConnection(connectionMode)
        }

        val pc = peerConnection ?: throw RuntimeException("Peer connection not initialized")

        // Create offer if peer connection is initialized
        return try {
            // Create the offer
            val offer = pc.createOffer()

            // Set as local description
            pc.setLocalDescription(
                RTCLocalSessionDescriptionInit(
                    sdp = offer.sdp,
                    type = offer.type
                )
            )

            // Wait for ICE gathering to complete
            withTimeoutOrNull(3000.milliseconds) {
                if (pc.iceGatheringState != RTCIceGatheringState.complete) {
                    suspendCancellableCoroutine { continuation ->
                        // Listen for ICE gathering to complete
                        pc.onicecandidate = EventHandler {
                            if (pc.iceGatheringState == RTCIceGatheringState.complete
                            ) {
                                continuation.resume(Unit)
                            }
                        }

                        // Cleanup on timeout/cancellation
                        continuation.invokeOnCancellation { pc.onicecandidate = null }
                    }
                }
            }

            val finalSdp = pc.localDescription?.sdp ?: offer.sdp
            val finalType = pc.localDescription?.type ?: offer.type

            WebRtcOfferAnswer(
                sdp = finalSdp.toString(),
                type = finalType.toString()
            )
        } catch (e: Exception) {
            throw Exception("JS, failed to create offer, error: ${e.message}")
        }
    }

    override suspend fun setRemoteAnswer(answer: WebRtcOfferAnswer) {
        val pc = peerConnection ?: throw RuntimeException("PeerConnection not initialized")

        try {
            // Create a JS object matching the RTCSessionDescriptionInit dictionary
            val remoteDescription = js("{}")
            remoteDescription.sdp = answer.sdp
            remoteDescription.type = answer.type

            pc.setRemoteDescription(remoteDescription)
        } catch (e: Exception) {
            throw Exception("JS, failed to set remoteDescription, error: ${e.message}")
        }
    }

    override suspend fun enableVideo(enable: Boolean) {
        if (enable) {
            // Ensure the stream is still attached
            videoElement.asDynamic().srcObject = remoteStream

            // Play the video
            videoElement.asDynamic().play().then({
                if (!framePumpStarted) {
                    framePumpStarted = true
                    startFramePump()
                }
            }, { e ->
                AppLogger.error("Failed to start video play, error: $e")
            })
        } else {
            framePumpStarted = false
            streamingJob?.cancel()
            streamingJob = null
            videoElement.pause()
            videoElement.asDynamic().srcObject = null
        }
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    override suspend fun disconnect() {
        // Stop video stream
        framePumpStarted = false
        streamingJob?.cancel()
        streamingJob = null
        videoElement.pause()
        videoElement.asDynamic().srcObject = null
        remoteStream.getTracks().forEach { track ->
            track.stop()
        }

        // Clear and close peer connection
        peerConnection?.close()
        peerConnection = null

        // Reset frame flow
        videoFrames.resetReplayCache()

        // Clear the DOM
        if (document.body.contains(videoElement)) {
            document.body.removeChild(videoElement)
        }
    }

    override fun getVideoStream(): Flow<ImageBitmap> = videoFrames

    private fun initializePeerConnection(mode: WebRtcConnectMode) {
        try {
            // Create a new RTCPeerConnection
            val config = when (mode) {
                WebRtcConnectMode.NORMAL -> {
                    js(
                        """
                        {
                            "iceServers": [
                                {"urls": "stun:stun.l.google.com:19302"},
                                {"urls": "stun:stun1.l.google.com:5349"},
                            ],
                            "sdpSemantics": "unified-plan"
                        }
                    """
                    )
                }

                WebRtcConnectMode.PRIVATE_TURN_SERVER -> {
                    js(
                        """
                        {
                            "iceServers": [
                                {
                                    "urls": "turn:20.162.218.32:3478?transport=udp",
                                    "username": "VreoTensor",
                                    "credential": "4JpzGlef4aMN3x0"
                                }
                            ],
                            "iceTransportPolicy": "relay",
                            "sdpSemantics": "unified-plan"
                        }
                    """
                    )
                }
            }

            // Create a new RTCPeerConnection
            peerConnection = RTCPeerConnection(config)

            // Setup video element
            remoteStream = MediaStream()
            videoElement.asDynamic().srcObject = remoteStream
            videoElement.autoplay = true
            videoElement.muted = true
            videoElement.setAttribute("playsinline", "true")

            // Style as 1x1 but in the DOM
            videoElement.style.cssText = """
                position: fixed;
                top: 0;
                left: 0;
                width: 100px; 
                height: 100px;
                z-index: -1000;
                pointer-events: none;
                opacity: 0.01; 
                visibility: visible;
            """.trimIndent()
            document.body.appendChild(videoElement)

            // Listen for the track from the target device
            peerConnection?.ontrack = EventHandler { event ->
                val track = event.track
                remoteStream.addTrack(track)
                videoElement.asDynamic().srcObject = remoteStream
            }

            // Add video track to the peer connection
            peerConnection?.addTransceiver(
                trackOrKind = "video",
                init = RTCRtpTransceiverInit(
                    direction = RTCRtpTransceiverDirection.recvonly
                )
            )

            peerConnection?.onconnectionstatechange = EventHandler {
                AppLogger.info("WebRTC: Connection state changed to ${peerConnection?.connectionState}")
            }
        } catch (e: Exception) {
            AppLogger.error("Failed to initialize peer connection, error: ${e.message}")
        }
    }

    private fun startFramePump() {
        streamingJob?.cancel()
        streamingJob = CoroutineScope(Dispatchers.Default).launch {
            delay(100.milliseconds)

            // Initialize canvas, it has to be used as middle man to acquire video stream data
            val canvas = document.createElement("canvas") as HTMLCanvasElement
            val ctx = canvas.getContext(
                contextId = CanvasRenderingContext2D.ID,
                options = js("{ willReadFrequently: true }")
            )

            if (ctx == null) {
                AppLogger.error("Failed to get canvas context")
                return@launch
            }

            var imageInfo = ImageInfo(
                height = 1920,
                width = 1080,
                colorType = ColorType.RGBA_8888,
                alphaType = ColorAlphaType.OPAQUE
            )

            var lastFrameTime = 0.0
            val frameInterval = 1000.0 / targetFps
            var frameBuffer: ByteArray? = null

            while (isActive && peerConnection != null && framePumpStarted) {
                // Wait for the next frame to be available
                window.awaitAnimationFrame()
                val now = window.performance.now()

                // Only process if enough time has passed, limit to 10 FPS
                if (now - lastFrameTime < frameInterval) continue
                lastFrameTime = now

                if (videoElement.videoWidth <= 0) continue

                if (validVideoStates.contains(videoElement.readyState)) {
                    // Adjust canvas and buffer to support the video frame size
                    if (canvas.width != videoElement.videoWidth ||
                        canvas.height != videoElement.videoHeight
                    ) {
                        canvas.width = videoElement.videoWidth
                        canvas.height = videoElement.videoHeight
                        frameBuffer = ByteArray(canvas.width * canvas.height * 4)
                        imageInfo = ImageInfo(
                            height = canvas.height,
                            width = canvas.width,
                            colorType = ColorType.RGBA_8888,
                            alphaType = ColorAlphaType.OPAQUE
                        )
                    }

                    // Draw the video frame to the canvas, this allows us to access the pixel data
                    ctx.drawImage(
                        videoElement,
                        0.0,
                        0.0,
                        canvas.width.toDouble(),
                        canvas.height.toDouble()
                    )

                    try {
                        val imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
                        val pixels = imageData.data

                        val buffer =
                            frameBuffer ?: ByteArray(pixels.length).also { frameBuffer = it }
                        buffer.asDynamic().set(pixels)

                        videoFrames.tryEmit(
                            bytesToImageBitmap(
                                data = buffer,
                                imageInfo = imageInfo
                            )
                        )
                    } catch (e: Exception) {
                        AppLogger.error("Failed to get image data, error: $e")
                    }
                }
            }
        }
    }

    /**
     * Bytes array to image bitmap converter. Defaults to full-hd resolution 1920x1080
     *
     * @param data frame/image data to be converted
     * @param imageInfo image information, including resolution and format
     * @return Image bitmap
     */
    private fun bytesToImageBitmap(
        data: ByteArray,
        imageInfo: ImageInfo = ImageInfo(
            height = 1920,
            width = 1080,
            colorType = ColorType.RGBA_8888,
            alphaType = ColorAlphaType.OPAQUE
        )
    ): ImageBitmap {
        return try {
            // Create a Skia Image from the raw bytes
            // rowBytes is width * 4 (since each pixel is 4 bytes: R, G, B, A)
            Image.makeRaster(
                imageInfo = imageInfo,
                bytes = data,
                rowBytes = imageInfo.width * 4
            ).toComposeImageBitmap()
        } catch (e: Exception) {
            throw RuntimeException(
                "JS, failed to convert bytes array to image bitmap, error: ${e.message}"
            )
        }
    }
}